import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition, Disclosure } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import {
  XMarkIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  BookmarkIcon,
  ShoppingBagIcon,
  IdentificationIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { BellAlertIcon } from "@heroicons/react/24/solid";
import { db, firebaseAuth } from "../firebase";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { useUser } from "../common/stateDefs";
import { useSidebar, SearchHistoryItem } from "../buyerStateDefs";
import logo from "../assets/img/kono-logo.svg";
import { classNames } from "../common/functions";
import {
  PURCHASE_ORDERS_CHANGED_EVENT,
  PurchaseOrderSummary,
  QUOTES_CHANGED_EVENT,
  QuoteSummary,
  claimGuestQuotes,
  listPurchaseOrders,
  listQuotes,
  loadQuote,
  loadQuoteFlowConfig,
} from "../quotes/api";
import { reopenInCart } from "../quotes/reopen";
import { collapseSearchHistory } from "./search-history-helpers";

/**
 * Rows shown, and documents read to fill them. A search run repeatedly is one
 * document per run, so the read has to be the wider of the two for the list to
 * reach back a useful way.
 */
const RECENT_SEARCHES_SHOWN = 12;
const RECENT_SEARCHES_FETCHED = 60;

type UploadedRfq = {
  id: string;
  filename?: string;
  queries?: string[];
  createdAt?: { seconds: number } | null;
};

type TrackedSearch = {
  id: string;
  query: string;
  urlQueryString?: string;
  direction?: "up" | "down" | "stable" | null;
  lastPrice?: number | null;
};

const PriceIndicator = ({
  direction,
}: {
  direction?: "up" | "down" | "stable" | null;
}) => {
  const color =
    direction === "down"
      ? "text-green-600"
      : direction === "up"
        ? "text-red-600"
        : "text-gray-900";
  const title =
    direction === "down"
      ? "Lowest price dropped since last check"
      : direction === "up"
        ? "Lowest price increased since last check"
        : "Lowest price unchanged (or awaiting first check)";
  return (
    <span className={classNames("font-bold", color)} title={title}>
      $
    </span>
  );
};

const NAV_LINK_CLASS =
  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-slate-100 hover:text-[#00062e]";

export const Sidebar = () => {
  const open = useSidebar((s) => s.sidebarOpen);
  const setOpen = useSidebar((s) => s.setSidebarOpen);
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const user = useUser((s) => s.user);
  const navigate = useNavigate();
  const isKonoTeam = (authUser?.email || "").trim().toLowerCase().endsWith("@kono.ai");

  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  const [rfqs, setRfqs] = useState<UploadedRfq[]>([]);
  const [tracked, setTracked] = useState<TrackedSearch[]>([]);
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [quotesAsGuest, setQuotesAsGuest] = useState(false);
  const [quotesOffered, setQuotesOffered] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderSummary[]>(
    [],
  );
  const [poOffered, setPoOffered] = useState(false);

  const trackedByQuery = useMemo(() => {
    const map = new Map<string, TrackedSearch>();
    tracked.forEach((t) => map.set(t.query, t));
    return map;
  }, [tracked]);

  // One row per search rather than one per document, and ordered here: a
  // document written before August 2026 can carry a timestamp Firestore cannot
  // sort meaningfully, so the query's own order is not to be trusted.
  const recentSearches = useMemo(
    () => collapseSearchHistory(history).slice(0, RECENT_SEARCHES_SHOWN),
    [history],
  );

  useEffect(() => {
    if (!authUser) {
      setHistory([]);
      return;
    }
    const ref = collection(db, `users/${authUser.uid}/user-search-history`);
    const unsub = onSnapshot(
      query(ref, orderBy("timestamp", "desc"), limit(RECENT_SEARCHES_FETCHED)),
      (snap) => {
        setHistory(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<SearchHistoryItem, "id">),
          })),
        );
      },
      (err) => console.error("search history subscription error", err),
    );
    return () => unsub();
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      setRfqs([]);
      return;
    }
    const ref = collection(db, `users/${authUser.uid}/uploaded-rfqs`);
    const unsub = onSnapshot(
      query(ref, orderBy("createdAt", "desc"), limit(20)),
      (snap) => {
        setRfqs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) })));
      },
      (err) => console.error("rfq subscription error", err),
    );
    return () => unsub();
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      setTracked([]);
      return;
    }
    const ref = collection(db, "tracked_searches");
    const unsub = onSnapshot(
      query(ref, where("uid", "==", authUser.uid)),
      (snap) => {
        setTracked(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as object) }) as TrackedSearch,
          ),
        );
      },
      (err) => console.error("tracked searches subscription error", err),
    );
    return () => unsub();
  }, [authUser]);

  // Saved quotes, including a guest's: the panel is what a returning visitor
  // recognises their quote by, so it is listed whether or not they signed in.
  //
  // Reloaded when a quote is saved and whenever the panel is opened, not only
  // on mount: a buyer who has just saved one from the cart opens this looking
  // for it, and a list assembled before they pressed the button would not have
  // it. The entry itself is shown as soon as quoting is on, so somebody with no
  // quotes yet can still find where they will live.
  useEffect(() => {
    if (authIsLoading) return;
    let cancelled = false;
    const load = async () => {
      try {
        const config = await loadQuoteFlowConfig();
        if (cancelled) return;
        setQuotesOffered(config.quote.show && config.quote.showInSidePanel);
        if (!config.quote.show || !config.quote.showInSidePanel) return;
        // Signing in is the moment both the account and the browser's id are in
        // hand, so it is the moment the guest's quotes can be handed over.
        if (authUser) await claimGuestQuotes();
        const listed = await listQuotes();
        if (cancelled) return;
        setQuotes(listed.quotes);
        setQuotesAsGuest(listed.asGuest);
      } catch {
        if (!cancelled) setQuotes([]);
      }
    };
    void load();
    const reload = () => void load();
    window.addEventListener(QUOTES_CHANGED_EVENT, reload);
    return () => {
      cancelled = true;
      window.removeEventListener(QUOTES_CHANGED_EVENT, reload);
    };
  }, [authIsLoading, authUser, open]);

  // Purchase orders, on the same terms as the quotes above: one raised a moment
  // ago has to be here when the panel is next opened.
  useEffect(() => {
    if (authIsLoading) return;
    let cancelled = false;
    const load = async () => {
      try {
        const config = await loadQuoteFlowConfig();
        if (cancelled) return;
        setPoOffered(config.po.show);
        if (!config.po.show) return;
        const listed = await listPurchaseOrders();
        if (cancelled) return;
        setPurchaseOrders(listed.purchaseOrders);
      } catch {
        if (!cancelled) setPurchaseOrders([]);
      }
    };
    void load();
    const reload = () => void load();
    window.addEventListener(PURCHASE_ORDERS_CHANGED_EVENT, reload);
    return () => {
      cancelled = true;
      window.removeEventListener(PURCHASE_ORDERS_CHANGED_EVENT, reload);
    };
  }, [authIsLoading, authUser, open]);

  const toggleTrack = async (q: string, urlQueryString?: string) => {
    if (!authUser || !q) return;
    const existing = trackedByQuery.get(q);
    try {
      if (existing) {
        await deleteDoc(doc(db, `tracked_searches/${existing.id}`));
      } else {
        await addDoc(collection(db, "tracked_searches"), {
          uid: authUser.uid,
          email: user?.email || authUser.email || null,
          query: q,
          urlQueryString: urlQueryString || "",
          active: true,
          createdAt: serverTimestamp(),
          lastPrice: null,
          direction: null,
        });
      }
    } catch (err) {
      console.error("Failed to toggle price tracking", err);
    }
  };

  const close = () => setOpen(false);

  const goTo = (to: string) => {
    close();
    navigate(to);
  };

  // Editing happens in the cart, which is the only editor a quote has. The
  // lines have to be fetched: the panel only holds each quote's headline.
  const editQuote = async (id: string) => {
    try {
      reopenInCart(await loadQuote(id));
      goTo("/cart");
    } catch (error) {
      console.error("Could not reopen quote", error);
    }
  };

  // Every document for that search, or the row reappears as soon as the
  // snapshot catches up with the one copy we deleted.
  const deleteHistory = async (ids: string[]) => {
    if (!authUser || ids.length === 0) return;
    try {
      await Promise.all(
        ids.map((id) =>
          deleteDoc(doc(db, `users/${authUser.uid}/user-search-history/${id}`)),
        ),
      );
    } catch (err) {
      console.error("Failed to delete search history item", err);
    }
  };

  const deleteRfq = async (id: string) => {
    if (!authUser) return;
    try {
      await deleteDoc(doc(db, `users/${authUser.uid}/uploaded-rfqs/${id}`));
    } catch (err) {
      console.error("Failed to delete RFQ", err);
    }
  };

  const runQuery = (q: string) => {
    goTo(`/?q=${encodeURIComponent(q)}`);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={close}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-y-0 left-0 flex max-w-full">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="flex h-full w-80 max-w-[85vw] flex-col bg-white shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4">
                  <Link to="/" onClick={close}>
                    <img src={logo} alt="Kono" className="h-7" />
                  </Link>
                  <button
                    onClick={close}
                    className="rounded-md p-1 text-gray-400 hover:bg-slate-100 hover:text-gray-700"
                    aria-label="Close menu"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-2 py-3">
                  <nav className="space-y-1">
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/")}
                    >
                      <HomeIcon className="h-5 w-5 text-gray-400" />
                      Search inventory
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/searches")}
                    >
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      My searches
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/bookmarked")}
                    >
                      <BookmarkIcon className="h-5 w-5 text-gray-400" />
                      Saved items
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/orders")}
                    >
                      <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
                      My orders
                    </button>
                    {quotesOffered && (
                      <button
                        className={classNames(NAV_LINK_CLASS, "w-full")}
                        onClick={() => goTo("/quotes")}
                      >
                        <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                        My quotes
                      </button>
                    )}
                    {poOffered && (
                      <button
                        className={classNames(NAV_LINK_CLASS, "w-full")}
                        onClick={() => goTo("/purchase-orders")}
                      >
                        <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-400" />
                        My purchase orders
                      </button>
                    )}
                    {!!authUser && (
                      <button
                        className={classNames(NAV_LINK_CLASS, "w-full")}
                        onClick={() => goTo("/my-information")}
                      >
                        <IdentificationIcon className="h-5 w-5 text-gray-400" />
                        My information
                      </button>
                    )}
                  </nav>

                  {/* Kono operators: the team's view of every customer's
                      RFQs, quotes and orders, next to their own. */}
                  {isKonoTeam && (
                    <div className="mt-2 border-t border-gray-100 px-2 pt-3">
                      <p className="px-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                        Kono team
                      </p>
                      {(
                        [
                          ["RFQs", "/rfq", DocumentTextIcon],
                          ["Quotes", "/quotes", ClipboardDocumentCheckIcon],
                          ["Orders", "/orders", ShoppingBagIcon],
                        ] as [string, string, typeof DocumentTextIcon][]
                      ).map(([label, path, Icon]) => (
                        <div key={path} className="flex items-center gap-1">
                          <span className={classNames(NAV_LINK_CLASS, "flex-1 cursor-default hover:bg-transparent")}>
                            <Icon className="h-5 w-5 text-gray-400" />
                            {label}
                          </span>
                          <button
                            className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-slate-100"
                            onClick={() => goTo(`${path}?scope=mine`)}
                            title={`${label} assigned to you or that you created`}
                          >
                            Mine
                          </button>
                          <button
                            className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-slate-100"
                            onClick={() => goTo(`${path}?scope=all`)}
                            title={`Every customer's ${label.toLowerCase()}`}
                          >
                            All
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Saved quotes, a guest's included */}
                  {quotesOffered && (
                    <SidebarSection
                      title={quotesAsGuest ? "Quotes (Guest)" : "Saved quotes"}
                      empty={quotes.length === 0}
                      emptyText="Take a quote from the cart and it is kept here, to edit or turn into a purchase order later."
                    >
                      {quotes.slice(0, 8).map((quote) => (
                        <li
                          key={quote.id}
                          className="rounded-md px-2 py-1.5 hover:bg-slate-50"
                        >
                          <button
                            className="w-full text-left"
                            onClick={() => goTo(`/quotes/${quote.id}`)}
                          >
                            <span className="block truncate text-sm text-gray-700 hover:text-[#00062e]">
                              {quote.reference}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {quote.createdAt.slice(0, 10)} ·{" "}
                              {quote.currency === "USD" ? "$" : ""}
                              {quote.totalPrice.toFixed(2)}
                              {quote.supersededBy ? " · replaced" : ""}
                            </span>
                          </button>
                          {!quote.supersededBy && (
                            <button
                              className="mt-0.5 text-xs text-blue-700 hover:underline"
                              onClick={() => void editQuote(quote.id)}
                            >
                              Edit
                            </button>
                          )}
                        </li>
                      ))}
                    </SidebarSection>
                  )}

                  {/* Purchase orders raised against those quotes */}
                  {poOffered && purchaseOrders.length > 0 && (
                    <SidebarSection
                      title="Purchase orders"
                      empty={false}
                      emptyText=""
                    >
                      {purchaseOrders.slice(0, 8).map((po) => (
                        <li
                          key={po.id}
                          className="rounded-md px-2 py-1.5 hover:bg-slate-50"
                        >
                          <button
                            className="w-full text-left"
                            onClick={() => goTo(`/purchase-orders/${po.id}`)}
                          >
                            <span className="block truncate text-sm text-gray-700 hover:text-[#00062e]">
                              {po.poNumber}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {po.createdAt.slice(0, 10)} ·{" "}
                              {po.currency === "USD" ? "$" : ""}
                              {po.totalPrice.toFixed(2)} ·{" "}
                              {po.status === "awaiting_payment"
                                ? "to pay"
                                : "placed"}
                            </span>
                          </button>
                        </li>
                      ))}
                    </SidebarSection>
                  )}

                  {/* Tracked searches */}
                  {!!authUser && tracked.length > 0 && (
                    <SidebarSection
                      title="Tracked searches"
                      empty={false}
                      emptyText=""
                    >
                      {tracked.map((t) => (
                        <li
                          key={t.id}
                          className="group flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50"
                        >
                          <span className="mr-2 w-3 text-center text-sm">
                            <PriceIndicator direction={t.direction} />
                          </span>
                          <button
                            className="flex-1 truncate text-left text-sm text-gray-700 hover:text-[#00062e]"
                            title={t.query}
                            onClick={() =>
                              t.urlQueryString
                                ? goTo(`/${t.urlQueryString}`)
                                : runQuery(t.query)
                            }
                          >
                            {t.query}
                          </button>
                          <button
                            onClick={() =>
                              toggleTrack(t.query, t.urlQueryString)
                            }
                            className="ml-2 shrink-0 text-[#009499] hover:text-[#00062e]"
                            aria-label="Stop tracking"
                            title="Stop tracking this search"
                          >
                            <BellAlertIcon className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </SidebarSection>
                  )}

                  {/* Recent searches */}
                  {!!authUser && (
                    <SidebarSection
                      title="Recent searches"
                      empty={recentSearches.length === 0}
                      emptyText="Your searches will show up here."
                    >
                      {recentSearches.map((search) => {
                        const h = search.item;
                        const isTracked = trackedByQuery.has(h.query);
                        return (
                          <li
                            key={search.key}
                            className="group flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50"
                          >
                            <button
                              className="flex-1 truncate text-left text-sm text-gray-700 hover:text-[#00062e]"
                              title={h.query}
                              onClick={() =>
                                h.urlQueryString
                                  ? goTo(`/${h.urlQueryString}`)
                                  : runQuery(h.query)
                              }
                            >
                              {h.query || "(empty search)"}
                            </button>
                            <button
                              onClick={() =>
                                toggleTrack(h.query, h.urlQueryString)
                              }
                              className={classNames(
                                "ml-2 shrink-0",
                                isTracked
                                  ? "text-[#009499] hover:text-[#00062e]"
                                  : "hidden text-gray-300 hover:text-[#009499] group-hover:block",
                              )}
                              aria-label={
                                isTracked ? "Stop tracking" : "Track price"
                              }
                              title={
                                isTracked
                                  ? "Stop tracking this search"
                                  : "Track price & inventory for this search"
                              }
                            >
                              {isTracked ? (
                                <BellAlertIcon className="h-4 w-4" />
                              ) : (
                                <BellIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteHistory(search.ids)}
                              className="ml-2 hidden shrink-0 text-gray-300 hover:text-red-500 group-hover:block"
                              aria-label="Delete search"
                              title={
                                search.count > 1
                                  ? `Delete this search (${search.count} times)`
                                  : "Delete this search"
                              }
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </li>
                        );
                      })}
                    </SidebarSection>
                  )}

                  {/* Uploaded RFQs */}
                  {!!authUser && (
                    <SidebarSection
                      title="Uploaded RFQs"
                      empty={rfqs.length === 0}
                      emptyText="Upload an RFQ from the search bar to keep it here."
                    >
                      {rfqs.map((r) => (
                        <li key={r.id} className="rounded-md px-1 py-1">
                          <div className="group flex items-center justify-between">
                            <DocumentArrowUpIcon className="mr-2 h-4 w-4 shrink-0 text-gray-400" />
                            <span
                              className="flex-1 truncate text-sm font-medium text-gray-700"
                              title={r.filename}
                            >
                              {r.filename || "RFQ"}
                            </span>
                            <button
                              onClick={() => deleteRfq(r.id)}
                              className="ml-2 hidden shrink-0 text-gray-300 hover:text-red-500 group-hover:block"
                              aria-label="Delete RFQ"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {!!r.queries?.length && (
                            <ul className="ml-6 mt-0.5 space-y-0.5">
                              {r.queries.slice(0, 8).map((q, i) => (
                                <li key={i}>
                                  <button
                                    className="truncate text-left text-xs text-gray-500 hover:text-[#009499] hover:underline"
                                    title={q}
                                    onClick={() => runQuery(q)}
                                  >
                                    {q}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </SidebarSection>
                  )}

                  {/* Info links */}
                  <div className="mt-4 border-t border-gray-100 pt-3">
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/about")}
                    >
                      <InformationCircleIcon className="h-5 w-5 text-gray-400" />
                      About us
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/faq")}
                    >
                      <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400" />
                      FAQ
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/privacy")}
                    >
                      <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                      Privacy policy
                    </button>
                    <button
                      className={classNames(NAV_LINK_CLASS, "w-full")}
                      onClick={() => goTo("/terms")}
                    >
                      <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                      Terms and conditions
                    </button>
                  </div>
                </div>

                {/* Profile footer */}
                <div className="border-t border-gray-100 p-3">
                  {authUser ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00062e] text-sm font-semibold text-white">
                          {(
                            user?.displayName ||
                            user?.email ||
                            authUser.email ||
                            "?"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-gray-900">
                            {user?.displayName || "My account"}
                          </div>
                          <div className="truncate text-xs text-gray-500">
                            {user?.email || authUser.email}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          close();
                          firebaseAuth.signOut();
                          navigate("/signout");
                        }}
                        className="shrink-0 rounded-md p-2 text-gray-400 hover:bg-slate-100 hover:text-red-500"
                        aria-label="Sign out"
                        title="Sign out"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => goTo("/signin")}
                      className="block w-full rounded-md border-2 border-[#00062e] py-2 text-center text-sm font-bold text-[#00062e] hover:bg-slate-100"
                    >
                      Sign in
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const SidebarSection = ({
  title,
  empty,
  emptyText,
  children,
}: {
  title: string;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) => (
  <Disclosure defaultOpen as="div" className="mt-4">
    {({ open }) => (
      <>
        <Disclosure.Button className="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600">
          {title}
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 transition",
              open ? "" : "-rotate-90",
            )}
          />
        </Disclosure.Button>
        <Disclosure.Panel>
          {empty ? (
            <p className="px-3 py-1 text-xs text-gray-400">{emptyText}</p>
          ) : (
            <ul className="mt-1 space-y-0.5 px-1">{children}</ul>
          )}
        </Disclosure.Panel>
      </>
    )}
  </Disclosure>
);
